/**
 * Minimal shared UI primitives for the shell features (WS4 scope only —
 * WS5/WS6 bring their own surfaces). Everything styles off tokens.css vars.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

import { useFocusLayer } from './useFocusLayer';

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-secondary" role="status">
      <span
        aria-hidden
        className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-(--border-strong) border-t-(--accent)"
      />
      {label !== undefined ? <span>{label}</span> : null}
    </span>
  );
}

export function SkeletonLine({ width = '100%', height = 12 }: { width?: string; height?: number }) {
  return <div aria-hidden className="lm-skeleton" style={{ width, height }} />;
}

/**
 * Enter submits (2026-09-05; owner: "pressing cmd + enter doesn't create.
 * That should apply throughout the app for submissions"): Cmd/Ctrl+Enter
 * from anywhere in the dialog, and a plain Enter from a single-line input,
 * both run the dialog's primary action. The action guards its own
 * readiness, so a half-filled form just does nothing.
 */
export function submitKey(e: React.KeyboardEvent, onSubmit: (() => void) | undefined): void {
  if (onSubmit === undefined || e.key !== 'Enter' || e.altKey || e.shiftKey) return;
  const target = e.target as HTMLElement;
  const inInput = target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'file';
  if (e.metaKey || e.ctrlKey || inInput) {
    e.preventDefault();
    onSubmit();
  }
}

/**
 * Modal dialog with backdrop; Esc and backdrop-click close. Focus is the
 * dialog's while it is open (useFocusLayer): it lands on the first field,
 * Tab stays inside, and it returns to the opener after.
 */
export function Dialog({
  open,
  onClose,
  onSubmit,
  title,
  children,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  /** the primary action, run by Cmd/Ctrl+Enter anywhere and Enter in an input */
  onSubmit?: () => void;
  title: string;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useFocusLayer(ref, open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      // The backdrop: a pointer shortcut for Escape, nothing a screen reader
      // needs to hear. Keys pressed inside the dialog bubble up to it.
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => submitKey(e, onSubmit)}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="max-h-[85vh] overflow-auto rounded-panel border border-default bg-raised p-6 shadow-overlay outline-none"
        style={{ width: wide ? 640 : 480 }}
      >
        <h2 className="mb-4 text-[length:var(--text-section-size)] leading-[var(--text-section-line)] font-semibold">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

export function Button({
  children,
  onClick,
  kind = 'default',
  disabled = false,
  type = 'button',
  title,
  testId,
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: 'default' | 'primary' | 'danger' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  testId?: string;
}) {
  const kindClasses = {
    default: 'border border-strong bg-raised hover:bg-inset text-primary',
    primary: 'border border-transparent bg-(--accent) text-(--bg-canvas) hover:opacity-90 font-medium',
    danger: 'border border-(--danger) text-(--danger) bg-transparent hover:bg-(--danger)/10',
    ghost: 'border border-transparent bg-transparent text-secondary hover:text-primary',
  }[kind];
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-card px-3 text-[length:var(--text-ui-size)] transition-colors duration-(--motion-hover) disabled:cursor-not-allowed disabled:opacity-50 ${kindClasses}`}
    >
      {children}
    </button>
  );
}

/**
 * THE text field (2026-09-05: one look everywhere; owner: "the buttons and
 * text input fields are inconsistent"). 32px tall like every button, the
 * inset fill, the default border, the strong border while focused (the
 * global focus ring draws on top for the keyboard).
 */
export const FIELD_CLASS =
  'w-full rounded-card border border-default bg-inset text-primary placeholder:text-tertiary transition-colors duration-(--motion-hover) focus:border-strong disabled:cursor-not-allowed disabled:opacity-50';

export function TextInput({
  id,
  value,
  onChange,
  placeholder,
  type = 'text',
  mono = false,
  onKeyDown,
  ariaLabel,
  testId,
  autoComplete,
  spellCheck,
  disabled = false,
}: {
  /** for a <label htmlFor>; the aria-label still names the field */
  id?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'email';
  mono?: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  ariaLabel?: string;
  testId?: string;
  autoComplete?: string;
  spellCheck?: boolean;
  disabled?: boolean;
}) {
  return (
    <input
      id={id}
      type={type}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      data-testid={testId}
      autoComplete={autoComplete}
      spellCheck={spellCheck}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      className={`h-8 px-3 ${FIELD_CLASS} ${mono ? 'mono' : ''}`}
    />
  );
}

/** A multi-line field with the TextInput's clothes (2026-09-03: the product description). */
export function TextArea({
  value,
  onChange,
  placeholder,
  rows = 3,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
  ariaLabel?: string;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      placeholder={placeholder}
      aria-label={ariaLabel ?? placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`resize-y px-3 py-2 ${FIELD_CLASS}`}
    />
  );
}

/** The plus that leads a "new" or "connect" action. */
export function PlusIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden className="shrink-0">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** The circular arrows of a refresh. */
export function RefreshIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
      <path d="M20 12a8 8 0 1 1-2.3-5.7" />
      <path d="M20 4v5h-5" />
    </svg>
  );
}

/**
 * THE icon-only button (2026-09-05; owner: "make use of icon buttons
 * more"). Three kinds that mirror Button's: `default` (outlined, for an
 * action on a row: refresh, replace), `danger` (outlined red, for remove and
 * disconnect), `ghost` (borderless, for close and dismiss). Two sizes: `md`
 * matches the 32px buttons and fields, `sm` sits inside a panel header. The
 * label is for assistive tech and the tooltip; never a bare glyph.
 */
export function IconButton({
  label,
  onClick,
  disabled = false,
  testId,
  children,
  className = '',
  kind = 'default',
  size = 'md',
}: {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  testId?: string;
  children: ReactNode;
  className?: string;
  kind?: 'default' | 'danger' | 'ghost';
  size?: 'md' | 'sm';
}) {
  const kindClass = {
    default: 'border border-strong bg-raised text-secondary hover:bg-inset hover:text-primary',
    danger: 'border border-(--danger) bg-transparent text-(--danger) hover:bg-(--danger)/10',
    ghost: 'border border-transparent bg-transparent text-tertiary hover:border-default hover:text-primary',
  }[kind];
  const sizeClass = size === 'sm' ? 'h-6 w-6 rounded-chip' : 'h-8 w-8 rounded-card';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`flex shrink-0 cursor-pointer items-center justify-center transition-colors duration-(--motion-hover) disabled:cursor-not-allowed disabled:opacity-50 ${sizeClass} ${kindClass} ${className}`}
    >
      {children}
    </button>
  );
}

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

export function CloseIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

export function TrashIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
    </svg>
  );
}

export function PencilIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

export function CopyIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </svg>
  );
}

export function CheckIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

/**
 * THE select (the refresh chip's job picker). Round 3 (owner, on the
 * settings and the composer): "make the model/effort selects look like the
 * app rather than raw browser selects."
 *
 * A real `<select>` underneath (keyboard, screen readers and the native
 * option list all come free), with the UA arrow suppressed and our own
 * chevron drawn in `currentColor`, so the control takes the theme like
 * everything else. Two shapes:
 *  - `field`: the settings-form control, full width of its slot;
 *  - `pill`: the compact one that lives inside the composer's input surface.
 */
export function Select({
  value,
  onChange,
  ariaLabel,
  title,
  disabled = false,
  testId,
  variant = 'field',
  children,
}: {
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  title?: string;
  disabled?: boolean;
  testId?: string;
  variant?: 'field' | 'pill';
  children: ReactNode;
}) {
  const shape =
    variant === 'field'
      ? 'h-8 w-full rounded-card border-default bg-inset px-2.5 pr-7 text-primary hover:border-strong'
      : 'h-7 max-w-52 rounded-pill border-default bg-transparent px-2.5 pr-6 text-[length:var(--text-micro-size)] text-secondary hover:border-(--accent) hover:text-primary';
  return (
    <span
      className={`relative inline-flex min-w-0 items-center ${variant === 'field' ? 'w-full' : ''}`}
    >
      <select
        value={value}
        aria-label={ariaLabel}
        title={title}
        disabled={disabled}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className={`min-w-0 cursor-pointer appearance-none truncate border transition-colors duration-(--motion-hover) disabled:cursor-not-allowed disabled:opacity-50 ${shape}`}
      >
        {children}
      </select>
      <span
        aria-hidden
        className={`pointer-events-none absolute ${
          variant === 'field' ? 'right-2.5' : 'right-2'
        } text-tertiary`}
      >
        ▾
      </span>
    </span>
  );
}

/**
 * Breadcrumb trail for the page header (e.g. Products / curium): every entry
 * with a `to` renders as a link, the last (current) entry as plain text.
 */
export function Breadcrumbs({
  items,
  testId,
}: {
  items: Array<{ label: string; to?: string }>;
  testId?: string;
}) {
  return (
    <nav aria-label="Breadcrumb" data-testid={testId ?? 'breadcrumbs'}>
      <ol className="m-0 flex list-none flex-wrap items-center gap-1.5 p-0 text-[length:var(--text-ui-size)] text-tertiary">
        {items.map((item, i) => (
          <li key={`${item.label}-${i}`} className="flex items-center gap-1.5">
            {i > 0 ? (
              <span aria-hidden className="text-tertiary">
                /
              </span>
            ) : null}
            {item.to !== undefined ? (
              <BreadcrumbLink to={item.to} label={item.label} />
            ) : (
              <span aria-current="page" className="text-secondary">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

/** Kept tiny + local so lib/ui stays router-importing in one place. */
function BreadcrumbLink({ to, label }: { to: string; label: string }) {
  return (
    <RouterLink to={to} className="text-tertiary no-underline hover:text-primary">
      {label}
    </RouterLink>
  );
}

/**
 * THE page frame for every app-shell page (Products, Product, Codebase,
 * History, product Settings, Account) — one max-width/margin system instead
 * of five ad-hoc ones (owner consistency round 2026-08-25: History rendered
 * full-bleed beside a centered Codebase page). Content pages and settings
 * pages, loading/error states included, all share this exact frame.
 */
export function PageContainer({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="mx-auto w-full max-w-6xl px-8 py-10" data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * Consistent page header for the app-shell pages (Products, Product, Repo,
 * Account): optional breadcrumb trail above, title left, optional meta line
 * under it, actions right.
 */
export function PageHeader({
  title,
  meta,
  actions,
  breadcrumbs,
}: {
  title: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Array<{ label: string; to?: string }>;
}) {
  return (
    <div className="mb-8 flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        {breadcrumbs !== undefined ? (
          <div className="mb-1">
            <Breadcrumbs items={breadcrumbs} />
          </div>
        ) : null}
        <h1 className="m-0 truncate text-[length:var(--text-title-size)] leading-[var(--text-title-line)] font-semibold">
          {title}
        </h1>
        {meta !== undefined ? <div className="mt-1 text-secondary">{meta}</div> : null}
      </div>
      {actions !== undefined ? (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      ) : null}
    </div>
  );
}

/**
 * A settings section: one card, one heading, an optional sentence saying
 * what the section is for, then the controls. Round 3 asked for tighter
 * rhythm across the settings pages; the way to get it is for both pages to
 * use the SAME block rather than each keeping its own near-copy, so the
 * heading-to-copy-to-control spacing can only be one thing.
 */
export function Section({
  title,
  description,
  children,
  testId,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="rounded-card border border-default bg-surface px-6 py-5" data-testid={testId}>
      <h2
        className={`mt-0 text-[length:var(--text-section-size)] leading-[var(--text-section-line)] font-semibold ${
          description === undefined ? 'mb-4' : 'mb-1'
        }`}
      >
        {title}
      </h2>
      {description !== undefined ? (
        <p className="mt-0 mb-4 text-secondary">{description}</p>
      ) : null}
      {children}
    </section>
  );
}

/** The width every settings control shares, so the column reads as a column. */
export const FIELD_WIDTH = 'w-full max-w-sm';

export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
      {children}
    </div>
  );
}

/** Inline error line styled off the danger token — for form/mutation errors. */
export function ErrorNote({ children }: { children: ReactNode }) {
  if (children === null || children === undefined || children === false) return null;
  return <div className="mt-2 text-[length:var(--text-ui-size)] text-(--danger)">{children}</div>;
}

/** A two-prong plug: a connection made by pasting a string, no vendor behind it. */
export function PlugIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <path d="M9 3v5M15 3v5M6 8h12v3a6 6 0 0 1-12 0V8zM12 17v4" />
    </svg>
  );
}

/** A cylinder: a data store of no particular make. */
export function DatabaseIcon({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} {...iconProps} className="shrink-0">
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12c0 1.7 3.6 3 8 3s8-1.3 8-3" />
    </svg>
  );
}

/**
 * The connection dot (2026-09-05): green while a source samples, the
 * "shifted" yellow when its last sample is stale, tertiary when off. Never
 * the only carrier of the state; the words next to it say it too.
 */
export function StatusDot({ status, size = 7 }: { status: 'ok' | 'stale' | 'off'; size?: number }) {
  const color =
    status === 'ok' ? 'var(--positive)' : status === 'stale' ? 'var(--shifted)' : 'var(--text-tertiary)';
  return (
    <span
      aria-hidden
      data-status={status}
      className="inline-block shrink-0 rounded-full"
      style={{ width: size, height: size, background: color }}
    />
  );
}
